import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, Stack, Link,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import VolverInicio from '../components/VolverInicio';
import { leerError } from '../utils/errores';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState(params.get('email') || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const passwordRestablecida = params.get('reset') === 'ok';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrorCode('');
    if (!email.trim() || !password) {
      setError('Ingresa tu email y tu contraseña.');
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate('/app');
    } catch (err: any) {
      const { message, code } = leerError(err, 'No pudimos iniciar tu sesión. Intenta de nuevo.');
      setError(message);
      setErrorCode(code || '');
    } finally {
      setLoading(false);
    }
  };

  const recuperarUrl = `/recuperar-contrasena${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ''}`;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, background: 'linear-gradient(135deg, #075E54, #25D366)' }}>
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <VolverInicio />
          <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <Logo size={44} />
            <Typography variant="h5" sx={{ pt: 1 }}>Iniciar sesión</Typography>
            <Typography variant="body2" color="text.secondary">El WhatsApp que responde solo, con IA</Typography>
          </Stack>
          {passwordRestablecida && !error && (
            <Alert severity="success" sx={{ mb: 2 }}>Tu contraseña se actualizó. Ingresa con tu nueva contraseña.</Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
              {errorCode === 'CREDENCIALES_INVALIDAS' && (
                <>
                  {' '}¿La olvidaste?{' '}
                  <Link component={RouterLink} to={recuperarUrl} fontWeight={600}>Recupérala aquí</Link>.
                </>
              )}
            </Alert>
          )}
          <form onSubmit={submit} noValidate>
            <Stack spacing={2}>
              <TextField label="Email" type="email" fullWidth required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <TextField label="Contraseña" type="password" fullWidth required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <Box sx={{ textAlign: 'right', mt: '-4px !important' }}>
                <Link component={RouterLink} to={recuperarUrl} variant="body2">¿Olvidaste tu contraseña?</Link>
              </Box>
              <Button type="submit" variant="contained" size="large" disabled={loading}>
                {loading ? 'Entrando...' : 'Iniciar sesión'}
              </Button>
            </Stack>
          </form>
          <Typography variant="body2" textAlign="center" sx={{ mt: 3 }}>
            ¿No tienes cuenta?{' '}
            <Link component={RouterLink} to="/registro">Regístrate gratis</Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
